/**
 * cron-helpers.js — Funções puras para conversão entre presets visuais e cronExpressions.
 *
 * Exports:
 *   presetToCron(preset, timezone?)       — preset object → cronExpression (UTC)
 *   cronToDescription(expr, t, timezone?) — cronExpression → human-readable i18n string (local time)
 *   isValidCron(expr)                     — cronExpression → boolean (com regra de 30 min mínimo)
 *   nextScheduledAt(expr, from?)          — cronExpression → próximo ISO timestamp
 */

// Ordem canônica dos dias da semana para sort em weekdays preset
const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

/**
 * Calcula o offset em horas de uma timezone IANA em relação a UTC.
 * Usa Intl.DateTimeFormat para obter o offset real (incluindo DST).
 *
 * @param {string} timezone — IANA timezone (ex: 'America/Sao_Paulo')
 * @returns {number} offset em horas (ex: -3 para BRT, +1 para CET)
 */
function getTimezoneOffsetHours(timezone) {
  if (!timezone || timezone === 'UTC') return 0;
  try {
    // Usa uma data fixa de referência para calcular o offset
    const now = new Date();
    const utcStr = now.toLocaleString('en-US', { timeZone: 'UTC' });
    const localStr = now.toLocaleString('en-US', { timeZone: timezone });
    const diffMs = new Date(localStr).getTime() - new Date(utcStr).getTime();
    return Math.round(diffMs / 3600000);
  } catch {
    return 0;
  }
}

/**
 * Converte hora local + timezone para hora UTC, retornando também o shift de dia.
 *
 * @param {number} localHour — hora no fuso do usuário (0-23)
 * @param {string} [timezone] — IANA timezone
 * @returns {{ utcHour: number, dayShift: number }} dayShift: -1, 0, ou +1
 */
function localHourToUtc(localHour, timezone) {
  const offset = getTimezoneOffsetHours(timezone);
  let utcHour = localHour - offset;
  let dayShift = 0;
  if (utcHour < 0) {
    utcHour += 24;
    dayShift = -1;
  } else if (utcHour >= 24) {
    utcHour -= 24;
    dayShift = 1;
  }
  return { utcHour, dayShift };
}

/**
 * Converte hora UTC para hora local na timezone informada.
 *
 * @param {number} utcHour — hora UTC (0-23)
 * @param {string} [timezone] — IANA timezone
 * @returns {{ localHour: number, dayShift: number }}
 */
function utcHourToLocal(utcHour, timezone) {
  const offset = getTimezoneOffsetHours(timezone);
  let localHour = utcHour + offset;
  let dayShift = 0;
  if (localHour < 0) {
    localHour += 24;
    dayShift = -1;
  } else if (localHour >= 24) {
    localHour -= 24;
    dayShift = 1;
  }
  return { localHour, dayShift };
}

/**
 * Desloca um array de dias da semana por dayShift posições.
 * Ex: ['MON','WED'] com dayShift=-1 → ['SUN','TUE']
 *
 * @param {string[]} days
 * @param {number} dayShift — -1, 0, ou +1
 * @returns {string[]}
 */
function shiftDays(days, dayShift) {
  if (dayShift === 0) return days;
  return days.map((d) => {
    const idx = DAY_ORDER.indexOf(d);
    if (idx === -1) return d;
    const newIdx = ((idx + dayShift) % 7 + 7) % 7;
    return DAY_ORDER[newIdx];
  });
}

/**
 * Converte um objeto preset em uma cronExpression válida (UTC).
 * Quando timezone é informada, converte a hora local para UTC.
 *
 * @param {{ type: string, hour?: number, hours?: number, days?: string[] }} preset
 * @param {string} [timezone] — IANA timezone do usuário (ex: 'America/Sao_Paulo')
 * @returns {string} cronExpression no formato rate() ou cron() (horas em UTC)
 */
function presetToCron(preset, timezone) {
  if (!preset || !preset.type) return '';

  switch (preset.type) {
    case 'interval': {
      const h = preset.hours;
      return h === 1 ? 'rate(1 hour)' : `rate(${h} hours)`;
    }

    case 'daily': {
      const { utcHour } = localHourToUtc(preset.hour, timezone);
      return `cron(0 ${utcHour} * * ? *)`;
    }

    case 'weekdays': {
      const { utcHour, dayShift } = localHourToUtc(preset.hour, timezone);
      const shifted = shiftDays(preset.days || [], dayShift);
      const sorted = [...shifted].sort(
        (a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b)
      );
      return `cron(0 ${utcHour} ? * ${sorted.join(',')} *)`;
    }

    default:
      return '';
  }
}

/**
 * Converte uma cronExpression em descrição legível no idioma ativo do usuário.
 * Quando timezone é informada, converte a hora UTC da expressão para hora local.
 *
 * @param {string} cronExpression
 * @param {Function} t — função i18next t()
 * @param {string} [timezone] — IANA timezone do usuário
 * @returns {string} descrição legível (horas em horário local)
 */
function cronToDescription(cronExpression, t, timezone) {
  if (!cronExpression || typeof t !== 'function') return '';

  // --- rate() expressions ---
  const rateMatch = cronExpression.match(
    /^rate\((\d+)\s+(minute|minutes|hour|hours|day|days)\)$/
  );
  if (rateMatch) {
    const n = parseInt(rateMatch[1], 10);
    const unit = rateMatch[2];

    if (unit === 'hour' || unit === 'hours') {
      return n === 1
        ? t('everyHour')
        : t('everyNHours', { n });
    }

    if (unit === 'day' || unit === 'days') {
      return n === 1
        ? t('everyDay')
        : t('everyNDays', { n });
    }

    if (unit === 'minute' || unit === 'minutes') {
      return n === 1
        ? t('everyMinute')
        : t('everyNMinutes', { n });
    }
  }

  // --- cron() expressions ---
  const cronMatch = cronExpression.match(
    /^cron\((\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+\*\)$/
  );
  if (cronMatch) {
    const [, , hourField, , , dowField] = cronMatch;
    const utcHour = parseInt(hourField, 10);
    const { localHour, dayShift } = utcHourToLocal(utcHour, timezone);
    const hour = String(localHour).padStart(2, '0');
    const time = `${hour}:00`;

    // Weekdays pattern: cron(0 H ? * DOW1,DOW2 *)
    if (dowField !== '?' && dowField !== '*') {
      const dayKeys = shiftDays(dowField.split(','), dayShift);
      const translatedDays = dayKeys
        .map((d) => t(`day.${d}`))
        .join(', ');
      return t('weekdaysAt', { days: translatedDays, time });
    }

    // Daily pattern: cron(0 H * * ? *)
    return t('dailyAt', { time });
  }

  // --- Fallback for unrecognized expressions ---
  return t('customSchedule', { expr: cronExpression });
}

/**
 * Valida uma cronExpression nos formatos rate() e cron().
 * Mesma lógica do backend cron-utils.js, com regra adicional:
 * rate() com unidade minutes e valor < 30 → false.
 *
 * @param {string} expr
 * @returns {boolean}
 */
function isValidCron(expr) {
  if (!expr) return false;

  // rate() validation
  const rateMatch = expr.match(
    /^rate\((\d+)\s+(minute|minutes|hour|hours|day|days)\)$/
  );
  if (rateMatch) {
    const n = parseInt(rateMatch[1], 10);
    const unit = rateMatch[2];
    // Frontend-only rule: minimum 30 minutes for rate() with minutes unit
    if ((unit === 'minute' || unit === 'minutes') && n < 30) {
      return false;
    }
    return true;
  }

  // cron() validation — same regex as backend
  if (/^cron\(\S+\s+\S+\s+\S+\s+\S+\s+\S+\s+\*\)$/.test(expr)) return true;

  return false;
}

/**
 * Calculates the next scheduled execution time from a cron/rate expression.
 * Port of backend/src/core/cron-utils.js nextScheduledAt() for frontend use.
 *
 * @param {string} expr — cronExpression in rate() or cron() format
 * @param {Date} [from=new Date()] — reference date
 * @returns {string|null} ISO timestamp or null
 */
function nextScheduledAt(expr, from = new Date()) {
  if (!expr) return null;

  if (expr.startsWith('rate(')) {
    const m = expr.match(/^rate\((\d+)\s+(minute|minutes|hour|hours|day|days)\)$/);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    const ms = n * (m[2].startsWith('minute') ? 60000 : m[2].startsWith('hour') ? 3600000 : 86400000);
    return new Date(from.getTime() + ms).toISOString();
  }

  if (expr.startsWith('cron(')) {
    const cronMatch = expr.match(/^cron\((\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+\*\)$/);
    if (!cronMatch) return null;
    const [, minF, hourF, domF, monF, dowF] = cronMatch;

    const matches = (field, val) => {
      if (field === '*' || field === '?') return true;
      if (field.includes('/')) return val % parseInt(field.split('/')[1], 10) === 0;
      if (field.includes('-')) {
        const [a, b] = field.split('-').map(Number);
        return val >= a && val <= b;
      }
      const DOW = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
      if (field.includes(',')) {
        return field.split(',').map((v) => DOW[v] !== undefined ? DOW[v] : Number(v)).includes(val);
      }
      return DOW[field] !== undefined ? DOW[field] === val : parseInt(field, 10) === val;
    };

    const next = new Date(from.getTime() + 60000);
    next.setSeconds(0, 0);
    const limit = new Date(from.getTime() + 7 * 86400000);
    while (next < limit) {
      if (
        matches(minF, next.getUTCMinutes()) &&
        matches(hourF, next.getUTCHours()) &&
        matches(domF, next.getUTCDate()) &&
        matches(monF, next.getUTCMonth() + 1) &&
        matches(dowF, next.getUTCDay())
      ) {
        return next.toISOString();
      }
      next.setMinutes(next.getMinutes() + 1);
    }
    return null;
  }

  return null;
}

export { presetToCron, cronToDescription, isValidCron, nextScheduledAt };
